import { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { Stethoscope, Mail, MessageCircle, Loader2, X, Eye, Lock, Pencil, Check, CalendarClock, Plus, Trash2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : "");
const inp = "w-full border border-line bg-white rounded-md px-3 py-2 text-sm outline-none focus:border-clinical";

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

export function AdminClinicianDirectory({ canEdit = false }) {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [slotFor, setSlotFor] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotForm, setSlotForm] = useState({ date: "", start_time: "", end_time: "" });
  const [slotBusy, setSlotBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/clinicians"); setList(data); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadSlots = async (c) => {
    try { const { data } = await api.get(`/clinicians/${c.user_id}/availability`); setSlots(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const openSlots = async (c) => { setSlotFor(c); setSlots([]); setSlotForm({ date: "", start_time: "", end_time: "" }); await loadSlots(c); };
  const addSlot = async () => {
    if (!slotForm.date || !slotForm.start_time || !slotForm.end_time) return toast.error("Fill date, start and end time.");
    setSlotBusy(true);
    try { await api.post(`/clinicians/${slotFor.user_id}/availability`, slotForm); setSlotForm({ date: "", start_time: "", end_time: "" }); await loadSlots(slotFor); toast.success("Slot added"); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSlotBusy(false); }
  };
  const delSlot = async (id) => {
    try { await api.delete(`/clinicians/${slotFor.user_id}/availability/${id}`); await loadSlots(slotFor); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const startEdit = (c) => setEditing({ ...c });
  const onPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const url = await uploadFile(f); setEditing((p) => ({ ...p, picture: url })); toast.success("Photo attached"); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clinicians/${editing.user_id}/profile`, {
        name: editing.name, title: editing.title, specialty: editing.specialty,
        bio: editing.bio, whatsapp: editing.whatsapp, email: editing.email || undefined,
        picture: editing.picture,
      });
      toast.success("Clinician profile updated");
      setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="clinician-directory">
      <h1 className="font-heading font-bold text-3xl tracking-tight text-ink flex items-center gap-2 mb-2">
        <Stethoscope className="w-7 h-7 text-clinical" /> Clinicians
      </h1>
      <p className="text-muted2 text-sm mb-6 inline-flex items-center gap-1.5">
        <Lock className="w-3.5 h-3.5" /> {canEdit ? "View and edit clinician profiles." : "Read-only directory — view full clinician profiles."}
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clinician-list">
        {list.length === 0 && <p className="text-muted2 text-sm">No clinician accounts yet. Create a Clinical Specialist account under Users.</p>}
        {list.map((c) => (
          <div key={c.user_id} data-testid="clinician-card" className="bg-white border border-line rounded-xl p-5">
            <div className="flex items-center gap-3">
              {c.picture ? (
                <img src={abs(c.picture)} alt={c.name} className="w-12 h-12 rounded-full object-cover" />
              ) : (
                <span className="w-12 h-12 rounded-full bg-clinical/10 text-clinical inline-flex items-center justify-center font-heading font-bold">
                  {(c.name || "?").slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="min-w-0">
                <p className="font-heading font-bold text-ink truncate">{c.title ? `${c.title} ` : ""}{c.name}</p>
                {c.specialty && <p className="text-sm text-muted2 truncate">{c.specialty}</p>}
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button data-testid="clinician-view-btn" onClick={() => setActive(c)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-clinical border border-clinical/30 rounded-md py-2 hover:bg-clinical/5">
                <Eye className="w-4 h-4" /> View
              </button>
              {canEdit && (
                <button data-testid="clinician-edit-btn" onClick={() => startEdit(c)}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-white bg-clinical rounded-md py-2 hover:bg-clinicaldark">
                  <Pencil className="w-4 h-4" /> Edit
                </button>
              )}
            </div>
            {canEdit && (
              <button data-testid="clinician-slots-btn" onClick={() => openSlots(c)}
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-ink border border-line rounded-md py-2 hover:bg-mist">
                <CalendarClock className="w-4 h-4 text-clinical" /> Availability slots
              </button>
            )}
          </div>
        ))}
      </div>

      {active && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" data-testid="clinician-profile-modal" onClick={() => setActive(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setActive(null)} data-testid="clinician-profile-close" className="absolute top-4 right-4 text-muted2 hover:text-ink"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-4">
              {active.picture ? (
                <img src={abs(active.picture)} alt={active.name} className="w-20 h-20 rounded-full object-cover" />
              ) : (
                <span className="w-20 h-20 rounded-full bg-clinical/10 text-clinical inline-flex items-center justify-center font-heading font-black text-2xl">
                  {(active.name || "?").slice(0, 1).toUpperCase()}
                </span>
              )}
              <div>
                <h2 className="font-heading font-bold text-xl text-ink" data-testid="clinician-profile-name">{active.title ? `${active.title} ` : ""}{active.name}</h2>
                {active.specialty && <p className="text-sm text-clinical font-semibold">{active.specialty}</p>}
              </div>
            </div>
            {active.bio && (
              <div className="mt-5">
                <p className="text-xs uppercase tracking-wider font-bold text-muted2 mb-1">Biography</p>
                <p className="text-sm text-ink whitespace-pre-line">{active.bio}</p>
              </div>
            )}
            <div className="mt-5 grid gap-2">
              <p className="text-xs uppercase tracking-wider font-bold text-muted2">Contact</p>
              <p className="text-sm text-ink inline-flex items-center gap-2" data-testid="clinician-profile-email"><Mail className="w-4 h-4 text-clinical" /> {active.email || <span className="text-muted2">Not provided</span>}</p>
              <p className="text-sm text-ink inline-flex items-center gap-2" data-testid="clinician-profile-whatsapp"><MessageCircle className="w-4 h-4 text-clinical" /> {active.whatsapp || <span className="text-muted2">Not provided</span>}</p>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" data-testid="clinician-edit-modal" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setEditing(null)} className="absolute top-4 right-4 text-muted2 hover:text-ink"><X className="w-5 h-5" /></button>
            <h2 className="font-heading font-bold text-xl text-ink mb-4">Edit clinician profile</h2>
            <div className="grid gap-3">
              <input className={inp} placeholder="Full name" data-testid="clin-edit-name" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <input className={inp} placeholder="Title (e.g. Dr, Nurse)" data-testid="clin-edit-title" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              <input className={inp} placeholder="Specialty" data-testid="clin-edit-specialty" value={editing.specialty || ""} onChange={(e) => setEditing({ ...editing, specialty: e.target.value })} />
              <textarea className={`${inp} resize-y`} rows={3} placeholder="Biography (max 100 words)" data-testid="clin-edit-bio" value={editing.bio || ""} onChange={(e) => setEditing({ ...editing, bio: e.target.value })} />
              <input className={inp} type="email" placeholder="Email" data-testid="clin-edit-email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
              <input className={inp} placeholder="WhatsApp number" data-testid="clin-edit-whatsapp" value={editing.whatsapp || ""} onChange={(e) => setEditing({ ...editing, whatsapp: e.target.value })} />
              <label className="inline-flex items-center gap-2 text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-2 cursor-pointer hover:bg-clinical/5 w-fit">
                {editing.picture ? "Change photo" : "Upload photo"}
                <input type="file" accept="image/*" onChange={onPhoto} className="hidden" />
              </label>
              {editing.picture && <img src={abs(editing.picture)} alt="" className="w-16 h-16 rounded-full object-cover" />}
              <button onClick={save} disabled={saving} data-testid="clin-edit-save" className="inline-flex items-center justify-center gap-1.5 bg-clinical text-white rounded-md py-2 px-4 text-sm font-semibold disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save profile
              </button>
            </div>
          </div>
        </div>
      )}
      {slotFor && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" data-testid="clinician-slots-modal" onClick={() => setSlotFor(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSlotFor(null)} className="absolute top-4 right-4 text-muted2 hover:text-ink"><X className="w-5 h-5" /></button>
            <h2 className="font-heading font-bold text-xl text-ink mb-1 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-clinical" /> Availability</h2>
            <p className="text-sm text-muted2 mb-4">{slotFor.title ? `${slotFor.title} ` : ""}{slotFor.name}</p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <input type="date" className={inp} data-testid="slot-date" value={slotForm.date} onChange={(e) => setSlotForm({ ...slotForm, date: e.target.value })} />
              <input type="time" className={inp} data-testid="slot-start" value={slotForm.start_time} onChange={(e) => setSlotForm({ ...slotForm, start_time: e.target.value })} />
              <input type="time" className={inp} data-testid="slot-end" value={slotForm.end_time} onChange={(e) => setSlotForm({ ...slotForm, end_time: e.target.value })} />
            </div>
            <button onClick={addSlot} disabled={slotBusy} data-testid="slot-add" className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md py-2 px-4 text-sm font-semibold disabled:opacity-60 mb-4">
              {slotBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add slot
            </button>
            <div className="space-y-2" data-testid="slot-list">
              {slots.length === 0 && <p className="text-sm text-muted2">No availability slots yet.</p>}
              {slots.map((s) => (
                <div key={s.id} data-testid="slot-row" className="flex items-center justify-between border border-line rounded-md px-3 py-2 text-sm">
                  <span className="text-ink">{s.date} · {s.start_time}–{s.end_time}</span>
                  <button onClick={() => delSlot(s.id)} data-testid="slot-delete" className="text-critical p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
